import Config, { defaultHeaders } from '../config';
import { RemoteIdAuth } from '../config/types';
import { 
  authenticatePasswordlessUserBySMSMutation,
  createPasswordlessUserByIdMutation,
  paymentMethodsQuery 
} from '../graphql/queries';

const headers = { ...defaultHeaders };

const { serviceUrl, appType, usersUrl, graphqlUrl, financeCoreUrl, authenticatePasswordlessScope } = Config;

export const authenticatePasswordlessUser = async (payload: Record<string, any>): Promise<any> =>
  fetch(graphqlUrl(), {
    method: 'POST',
    body: JSON.stringify({
      "query": authenticatePasswordlessUserBySMSMutation,
      "variables": {
        "input": {
          "code": payload.code,
          "mobile": payload.mobile,
          "scope": authenticatePasswordlessScope.join(" "),
          "audience": graphqlUrl()
        }
      }
    }),
    headers,
  })

export const createPasswordlessUserById = async (payload: RemoteIdAuth): Promise<any> =>
  fetch(`${usersUrl()}/graphql`, {
    method: 'POST',
    body: JSON.stringify({
      "query": createPasswordlessUserByIdMutation,
      "variables": {
          "input": {
              "userId": payload.remoteId,
          }
      }
    }),
    headers,
  });

export const refreshToken = async (refreshToken: string): Promise<any> => {
  return fetch(`${serviceUrl()}/identity/refresh`, {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
    headers,
  });
};

export const listCpayMethods = async (payload: Record<string, any>): Promise<any> => {
  const { accessToken, userId } = payload;
  // Route directly to finance-core (which owns the paymentMethods union and accepts the
  // legacy Brighte token) rather than the Hive gateway, whose genericAuth protect-all only
  // trusts Auth0 RS256 tokens and rejects our token with UNAUTHORIZED_FIELD_OR_TYPE.
  // See MON-70: revert to graphqlUrl() once this app issues Auth0 tokens.
  const response = await fetch(`${financeCoreUrl()}/graphql`, {
    method: 'POST',
    body: JSON.stringify({
      "query": paymentMethodsQuery,
      "variables": {
        "userId": `U${userId}`,
      }
    }),
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
  });
  const paymentMethods = await response.json();
  if (paymentMethods.errors) {
    throw new Error(paymentMethods.errors[0].message);
  }

  return paymentMethods?.data?.paymentMethods || [];
}
  

export const updatePaymentMethods = async (payload: Record<string, any>): Promise<any> => {
  const { accessToken, id, cardExpiry, userId } = payload;

  return fetch(`${serviceUrl()}/payment-methods/${id}?userId=${userId}`, {
    method: 'PUT',
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ cardExpiry }),
  });
};

export const getCreditCardUrl = async (): Promise<Response> =>
  fetch(`${serviceUrl()}/payment-gateway/iframe?source=${appType}`, {
    method: 'GET',
    headers,
  });

export const savePaymentMethod = async (payload: Record<string, any>): Promise<any> => {
  const { accessToken } = payload;
  delete payload.accessToken;

  return fetch(`${serviceUrl()}/payment-methods`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
  });
};

export const getCpayDetails = async (payload: Record<string, any>): Promise<any> => {
  const { cpayMethodUrl, accessToken, userId } = payload;

  return fetch(`${cpayMethodUrl}?userId=${userId}`, {
    method: 'GET',
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
  });
};

export const submitApp = async (payload: Record<string, any>): Promise<any> => {
  const { accessToken } = payload;
  delete payload.accessToken;

  return fetch(`${serviceUrl()}/payment-gateway/pay`, {
    method: 'POST',
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
};

// TODO test only
export const testSubmitApp = async (payload: Record<string, any>): Promise<any> =>
  new Promise<any>((resolve, reject) => {
    setTimeout(() => {
      resolve({ status: 400, message: 'some data' });
    }, 3000);
  });

export default {
  getCreditCardUrl,
  savePaymentMethod,
  getCpayDetails,
  submitApp,
  testSubmitApp,
};
